<template>
  <s-page-block>
    <s-page-header
      :heading="`Response ${taskResponseEvaluation.response.id} for ${taskEvaluation.title}`"
    ></s-page-header>
    <s-btn class="mb-4 mt-4" size="lg" color="secondary">
      <s-link
        method="post"
        class="link-subtle"
        :href="
          route('admin.tasks.responses.edit', {
            task: props.task.id,
            response: referenceResponse.id,
          })
        "
      >
        View/Edit Original Response (Reference)
      </s-link>
    </s-btn>
    <s-details>
      <template #summary>Task Response Evaluation Meta Data</template>
      <pre>
        <code>
          {{ JSON.stringify(taskResponseEvaluation.response, null, 2) }}
        </code>
      </pre>
    </s-details>
    <s-details>
      <template #summary>Student Response:</template>
      <pre>
        <code>
          {{ JSON.stringify(referenceResponse, null, 2) }}
        </code>
      </pre>
    </s-details>
    <s-details>
      <template #summary>Evaluation Result:</template>
      <pre>
        <code>
          {{ JSON.stringify(evaluationResponse, null, 2) }}
        </code>
      </pre>
    </s-details>
    <s-details>
      <template #summary>Assessment Comparison</template>
      <div class="flex flex-col gap-4 md:flex-row md:gap-6">
        <!-- Reference Response -->
        <div class="flex-1 border rounded-xl bg-white/20">
          <div class="p-4 border-b font-medium text-center">Reference (Original)</div>
          <s-task-grade-pill :grade="referenceGrade" :weight="100" class="px-4 pt-4" />
          <data-driven-feedback
            v-if="task.taskType === 'DataDrivenTask' && taskRubric"
            :rubric="taskRubric"
            :feedback-by-parts="referenceFeedbackByPart"
            :grades="referencePartGrades"
            :task-state="taskState"
          />
          <feedback-by-part
            v-else
            :feedback-by-part="referenceFeedbackByPart"
            :part-grades="referencePartGrades"
            :task-state="taskState"
            class="px-5 2xl:px-7 py-2.5 first:pt-3 last:pb-3 bg-gradient-to-b from-white/30 via-transparent"
          />
        </div>

        <!-- Evaluation Response -->
        <div class="flex-1 border rounded-xl bg-white/20">
          <div class="p-4 border-b font-medium text-center">Evaluation</div>
          <s-task-grade-pill :grade="evaluationGrade" :weight="100" class="px-4 pt-4" />
          <data-driven-feedback
            v-if="task.taskType === 'DataDrivenTask' && taskRubric"
            :rubric="taskRubric"
            :feedback-by-parts="evaluationFeedbackByPart"
            :grades="evaluationPartGrades"
            :task-state="taskState"
          />
          <feedback-by-part
            v-else
            :feedback-by-part="evaluationFeedbackByPart"
            :part-grades="evaluationPartGrades"
            :task-state="taskState"
            class="px-5 2xl:px-7 py-2.5 first:pt-3 last:pb-3 bg-gradient-to-b from-white/30 via-transparent"
          />
        </div>
      </div>
    </s-details>
  </s-page-block>
</template>
<script setup lang="ts">
import SPageBlock from '../../../design-system/SPageBlock.vue';
import SPageHeader from '../../../design-system/SPageHeader.vue';
import {Task, TaskFeedback, TaskState} from '../../../types/entities/tasks';
import {useBreadcrumbs} from '../../../composables/useBreadcrumbs';
import {route} from 'ziggy-js';
import SLink from '../../../components/SLink.vue';
import SDetails from '../../../design-system/SDetails.vue';
import FeedbackByPart from '../../AssignmentTask/Show/FeedbackByPart.vue';
import STaskGradePill from '../../../components/STaskGradePill.vue';
import SBtn from '../../../design-system/SBtn.vue';
import DataDrivenFeedback from '../../AssignmentMarking/Show/DataDrivenFeedback.vue';
import TaskEvaluationDto = App.DTOs.TaskEvaluationDto;
import TaskResponseEvaluationDto = App.DTOs.TaskResponseEvaluationDto;
import TaskResponseDto = App.DTOs.Entities.Tasks.TaskResponseDto;
import TaskGradeDto = App.DTOs.TaskGradeDto;
import TaskCriterionGradeDto = App.DTOs.Entities.Grades.TaskCriterionGradeDto;
import Rubric = App.DTOs.Rubrics.Rubric;

const props = defineProps<{
  task: Task;
  taskState: TaskState;
  taskRubric: Rubric | null;
  taskEvaluation: TaskEvaluationDto;
  taskResponseEvaluation: TaskResponseEvaluationDto;
  evaluationResponse: TaskResponseDto;
  referenceResponse: TaskResponseDto;
  evaluationGrade: TaskGradeDto;
  referenceGrade: TaskGradeDto;
  evaluationPartGrades: TaskCriterionGradeDto[];
  referencePartGrades: TaskCriterionGradeDto[];
  evaluationFeedbackByPart: Record<string, TaskFeedback[]>;
  referenceFeedbackByPart: Record<string, TaskFeedback[]>;
}>();

useBreadcrumbs([
  {label: 'Tasks', href: route('admin.tasks.index')},
  {label: props.task.title, href: route('admin.tasks.show', {task: props.task.id})},
  {label: 'Evaluations', href: route('admin.tasks.evaluations.index', {task: props.task.id})},
  {
    label: props.taskEvaluation.title,
    href: route('admin.tasks.evaluations.show', {
      task: props.task.id,
      taskEvaluation: props.taskEvaluation.id,
    }),
  },
]);
</script>
